import { computed, makeAutoObservable } from 'mobx';
import { EcomSDKErrorLike } from '../types/EcomSDK';
import {
    AmountParseError,
    AmountValidationError,
    formatMinorAmount,
    fromMinorUnits,
    parseAmountInput,
    toMinorUnits,
    validateAmount,
} from './customAmount';
import { CustomPricingConfig } from './customPricing';

export const CUSTOM_AMOUNT_SDK_ERROR_PREFIX = 'sdk_errors.custom_amount_';

export type CustomAmountError =
    | 'required'
    | Exclude<AmountParseError, 'empty'>
    | AmountValidationError;

export interface CustomAmountServerError {
    code: string;
    params?: Record<string, string | number>;
}

interface Evaluation {
    minor: number | null;
    error: CustomAmountError | null;
}

/**
 * The amount a shopper chose for a pay-what-you-want product. Shared, through
 * `ProductState`, by the price-options widget (which writes it) and the price
 * and add-to-cart widgets (which read it).
 *
 * With the free-entry field, the field is the single source of the amount:
 * picking a suggestion fills it, and typing another amount deselects it.
 */
class CustomAmountState {
    readonly config: CustomPricingConfig;
    input = '';
    selectedIndex: number | null = null;
    error: CustomAmountError | null = null;
    serverError: CustomAmountServerError | null = null;
    /** Pickers showing this amount: add-to-cart only blocks on its validation while there's one. */
    pickerCount = 0;

    constructor(config: CustomPricingConfig) {
        this.config = config;
        makeAutoObservable(this, {
            config: false,
            evaluation: computed,
            amountMinor: computed,
        });
    }

    get evaluation(): Evaluation {
        const { allowCustomAmount, suggestedAmounts, decimals, format } =
            this.config;
        if (!allowCustomAmount) {
            const suggestion =
                this.selectedIndex === null
                    ? undefined
                    : suggestedAmounts[this.selectedIndex];
            return {
                minor: suggestion
                    ? toMinorUnits(suggestion.amount, decimals)
                    : null,
                error: null,
            };
        }

        const parsed = parseAmountInput(
            this.input,
            decimals,
            format.decimalSeparator
        );
        if ('error' in parsed) {
            return {
                minor: null,
                error: parsed.error === 'empty' ? null : parsed.error,
            };
        }
        const error = validateAmount(parsed.minor, this.config);
        return error
            ? { minor: null, error }
            : { minor: parsed.minor, error: null };
    }

    /** The valid amount in minor units, or null. */
    get amountMinor(): number | null {
        return this.evaluation.minor;
    }

    /** The valid amount in major units, as the SDK takes it, or null. */
    get amount(): number | null {
        return this.amountMinor === null
            ? null
            : fromMinorUnits(this.amountMinor, this.config.decimals);
    }

    get formattedAmount(): string | null {
        return this.amountMinor === null
            ? null
            : formatMinorAmount(
                  this.amountMinor,
                  this.config.decimals,
                  this.config.format
              );
    }

    selectSuggestion = (index: number) => {
        const suggestion = this.config.suggestedAmounts[index];
        if (!suggestion) {
            return;
        }
        this.selectedIndex = index;
        if (this.config.allowCustomAmount) {
            const { decimals, format } = this.config;
            this.input = formatMinorAmount(
                toMinorUnits(suggestion.amount, decimals),
                decimals,
                format,
                { forInput: true }
            );
        }
        this.error = null;
        this.serverError = null;
    };

    setInput = (input: string) => {
        this.input = input;
        this.serverError = null;

        const { decimals, format, suggestedAmounts } = this.config;
        const parsed = parseAmountInput(
            input,
            decimals,
            format.decimalSeparator
        );
        const index =
            'minor' in parsed
                ? suggestedAmounts.findIndex(
                      ({ amount }) =>
                          toMinorUnits(amount, decimals) === parsed.minor
                  )
                : -1;
        this.selectedIndex = index === -1 ? null : index;

        // Once an error is showing, keep it in step with the field.
        if (this.error) {
            this.validate();
        }
    };

    /** Shows the matching error, if any. On blur, and with `requireAmount` on add-to-cart. */
    validate = ({ requireAmount = false } = {}): boolean => {
        const { minor, error } = this.evaluation;
        if (error) {
            this.error = error;
            return false;
        }
        if (minor === null && requireAmount) {
            this.error = 'required';
            return false;
        }
        this.error = null;
        return true;
    };

    setServerError = ({ code, params }: EcomSDKErrorLike) => {
        this.error = null;
        this.serverError = { code, params };
    };

    get hasPicker(): boolean {
        return this.pickerCount > 0;
    }

    /** For a picker's effect: counts it while mounted, and returns the cleanup. */
    attachPicker = () => {
        this.pickerCount += 1;
        return this.detachPicker;
    };

    detachPicker = () => {
        this.pickerCount -= 1;
    };
}

export default CustomAmountState;
